// =============================================================================
// Wiki Repository - Fetch & mutate wiki documents
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { DocDocument, DocScopeType, DocVisibility, DocPermissionLevel, DocSharedMember } from '../types';

export async function fetchWikiDocuments(orgId: string): Promise<DocDocument[]> {
  const [docsResult, permsResult] = await Promise.all([
    supabase
      .from('docs_documents')
      .select(`
        *,
        creator:core_profiles!docs_documents_created_by_fkey(full_name, avatar_url),
        updater:core_profiles!docs_documents_updated_by_fkey(full_name),
        members:docs_document_members(count),
        shared:docs_document_members(
          org_member_id,
          member:core_organization_members!docs_document_members_org_member_id_fkey(
            profile:core_profiles!core_organization_members_user_id_fkey(full_name, avatar_url)
          )
        )
      `)
      .eq('org_id', orgId)
      .eq('is_wiki', true)
      .eq('is_archived', false)
      .order('updated_at', { ascending: false }),
    supabase.rpc('get_documents_permissions', { p_org_id: orgId }),
  ]);

  if (docsResult.error) throw docsResult.error;

  // Build permission map
  const permMap: Record<string, string> = {};
  if (!permsResult.error && permsResult.data) {
    (permsResult.data as any[]).forEach((p: any) => {
      permMap[p.document_id] = p.permission;
    });
  }

  const docs = (docsResult.data || []).map((d: any) => ({
    ...d,
    doc_type: 'document' as const, // Normalise : un wiki est toujours doc_type='document'
    creator_name: d.creator?.full_name || null,
    creator_avatar: d.creator?.avatar_url || null,
    updater_name: d.updater?.full_name || null,
    member_count: d.members?.[0]?.count ?? 0,
    shared_members: ((d.shared || []) as any[])
      .map((s: any) => {
        const profile = Array.isArray(s.member?.profile) ? s.member.profile[0] : s.member?.profile;
        return profile ? { full_name: profile.full_name, avatar_url: profile.avatar_url } as DocSharedMember : null;
      })
      .filter(Boolean) as DocSharedMember[],
    user_permission: (permMap[d.id] as DocPermissionLevel) || null,
  }));

  // Resolve scope names for space/project scoped documents
  const scopeIds = {
    space: [...new Set(docs.filter(d => d.scope_type === 'space' && d.scope_id).map(d => d.scope_id))],
    project: [...new Set(docs.filter(d => d.scope_type === 'project' && d.scope_id).map(d => d.scope_id))],
  };

  const scopeNames: Record<string, string> = {};

  if (scopeIds.space.length > 0) {
    const { data: spaces } = await supabase
      .from('project_spaces')
      .select('id, name')
      .in('id', scopeIds.space);
    spaces?.forEach(s => { scopeNames[s.id] = s.name; });
  }

  if (scopeIds.project.length > 0) {
    const { data: projects } = await supabase
      .from('project_projects')
      .select('id, name')
      .in('id', scopeIds.project);
    projects?.forEach(p => { scopeNames[p.id] = p.name; });
  }

  return docs.map(d => ({
    ...d,
    scope_name: d.scope_id ? scopeNames[d.scope_id] || null : null,
  }));
}

export async function convertToWiki(docId: string, updatedBy: string): Promise<void> {
  const { error } = await supabase
    .from('docs_documents')
    .update({
      is_wiki: true,
      doc_type: 'document', // Exclusivité : un wiki est toujours doc_type='document'
      updated_by: updatedBy,
      updated_at: new Date().toISOString(),
    })
    .eq('id', docId);

  if (error) throw error;
}

export async function convertFromWiki(docId: string, updatedBy: string): Promise<void> {
  const { error } = await supabase
    .from('docs_documents')
    .update({
      is_wiki: false,
      updated_by: updatedBy,
      updated_at: new Date().toISOString(),
    })
    .eq('id', docId);

  if (error) throw error;
}

export async function updateWikiPosition(docId: string, position: number): Promise<void> {
  const { error } = await supabase
    .from('docs_documents')
    .update({ wiki_position: position })
    .eq('id', docId);

  if (error) throw error;
}

export async function markDocumentAsWiki(docId: string, isWiki: boolean): Promise<void> {
  const { error } = await supabase
    .from('docs_documents')
    .update({ is_wiki: isWiki })
    .eq('id', docId);

  if (error) throw error;
}

export async function fetchDocumentPermission(orgId: string, docId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_documents_permissions', {
    p_org_id: orgId,
  });
  if (error) {
    console.error('Error fetching permissions:', error);
    return null;
  }
  const perm = (data as any[])?.find((p: any) => p.document_id === docId);
  return perm?.permission || null;
}

export async function searchWikiPages(orgId: string, query: string): Promise<any[]> {
  const q = `%${query}%`;
  
  // Search in wiki document titles
  const { data: docs, error: docsErr } = await supabase
    .from('docs_documents')
    .select('id, title, icon, updated_at')
    .eq('org_id', orgId)
    .eq('is_wiki', true)
    .eq('is_archived', false)
    .ilike('title', q)
    .limit(10);

  if (docsErr) throw docsErr;

  // Search in pages of wiki documents — title match
  const { data: titlePages, error: titleErr } = await supabase
    .from('docs_pages')
    .select(`
      id, title, document_id, updated_at, content,
      document:docs_documents!docs_pages_document_id_fkey(id, title, icon, is_wiki)
    `)
    .eq('org_id', orgId)
    .eq('is_archived', false)
    .ilike('title', q)
    .limit(15);

  if (titleErr) throw titleErr;

  // Search in pages content (cast JSON content to text for ilike search)
  const { data: contentPages, error: contentErr } = await supabase
    .from('docs_pages')
    .select(`
      id, title, document_id, updated_at,
      document:docs_documents!docs_pages_document_id_fkey(id, title, icon, is_wiki)
    `)
    .eq('org_id', orgId)
    .eq('is_archived', false)
    .ilike('content::text', q)
    .limit(10);

  if (contentErr) {
    console.warn('Content search failed:', contentErr);
  }

  // Merge and deduplicate pages
  const allPages = [...(titlePages || []), ...(contentPages || [])];
  const seenPageIds = new Set<string>();
  const uniquePages = allPages.filter((p: any) => {
    if (seenPageIds.has(p.id)) return false;
    seenPageIds.add(p.id);
    return true;
  });

  // Filter pages that belong to wiki documents
  const wikiPages = uniquePages.filter((p: any) => p.document?.is_wiki === true);

  return [
    ...(docs || []).map((d: any) => ({ type: 'document' as const, ...d })),
    ...wikiPages.map((p: any) => ({
      type: 'page' as const,
      id: p.id,
      title: p.title,
      document_id: p.document_id,
      document_title: p.document?.title,
      document_icon: p.document?.icon,
      updated_at: p.updated_at,
    })),
  ];
}
